/**
 * EventsWidget (spec 015 · T070).
 *
 * The "For you right now" launcher widget panel for LocalTea.
 *
 * Faithful to `tmp/platform-mockups/launcher.html`:
 *   - Panel card with title "Tonight nearby" + mono eyebrow "localtea · picks"
 *   - One row per event:
 *       [day / month date block]  title + venue · time  [category badge]
 *   - Categories drive the pill color via a [data-cat] attribute so CSS
 *     stays in launcher-home.css.
 *
 * Props shape matches `LauncherTonightEvent` from
 * `frontend/src/services/launcher.ts`. `start_date` is an ISO datetime
 * string (despite the column name `start_date` — it's a datetime).
 */
import { CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { LauncherTonightEvent } from '@/services/launcher';

interface EventsWidgetProps {
  events: LauncherTonightEvent[];
}

const MONTHS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

function formatDay(iso: string): { day: string; month: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: '—', month: '' };
  return {
    day: String(d.getDate()),
    month: MONTHS[d.getMonth()] ?? '',
  };
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h24 = d.getHours();
  const h12 = ((h24 + 11) % 12) + 1;
  const mm = d.getMinutes();
  const meridiem = h24 >= 12 ? 'pm' : 'am';
  return mm === 0 ? `${h12} ${meridiem}` : `${h12}:${String(mm).padStart(2, '0')} ${meridiem}`;
}

function primaryCategory(
  cats: { slug: string; label: string }[] | undefined,
): { slug: string; label: string } {
  return cats && cats.length > 0 ? cats[0] : { slug: 'event', label: 'Event' };
}

function EventRow({ event }: { event: LauncherTonightEvent }) {
  const { day, month } = formatDay(event.start_date);
  const time = formatTime(event.start_date);
  const cat = primaryCategory(event.categories);

  return (
    <Link
      to={`/apps/localtea/event/${event.id}`}
      className="event-row"
      data-testid={`event-row-${event.id}`}
    >
      <div className="event-row__date" aria-hidden="true">
        <div className="event-row__date-day">{day}</div>
        <div className="event-row__date-month">{month}</div>
      </div>
      <div className="event-row__body">
        <h4 className="event-row__title">{event.title}</h4>
        <div className="event-row__meta">
          {event.venue_name}
          {time ? ` · ${time}` : ''}
          {event.price_display ? ` · ${event.price_display}` : ''}
        </div>
      </div>
      <span className="event-row__cat" data-cat={cat.slug}>
        {cat.label}
      </span>
    </Link>
  );
}

function EventsEmpty() {
  return (
    <div className="widget__empty">
      <p className="widget__empty-line">Nothing lined up in your radius tonight.</p>
      <Link to="/apps/localtea" className="widget__empty-cta">
        Browse LocalTea →
      </Link>
    </div>
  );
}

export default function EventsWidget({ events }: EventsWidgetProps) {
  return (
    <section
      className="widget widget--events"
      aria-labelledby="widget-events-title"
    >
      <header className="widget__head">
        <span className="widget__logo" aria-hidden="true">
          <CalendarDays />
        </span>
        <div className="widget__title-block">
          <h3 id="widget-events-title" className="widget__title">
            Tonight nearby
          </h3>
          <div className="widget__sub">localtea · picks</div>
        </div>
        <Link to="/apps/localtea" className="widget__link">
          All →
        </Link>
      </header>
      <div className="widget__body">
        {events.length === 0 ? (
          <EventsEmpty />
        ) : (
          events.map((e) => <EventRow key={e.id} event={e} />)
        )}
      </div>
    </section>
  );
}
